import React, { useEffect, useState } from 'react';
import { integrations } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { Settings, Camera, MessageCircle, Briefcase, Users, Check, Unplug, CloudRain } from 'lucide-react';

const platformIcon = (p) => {
  const props = {size:28, strokeWidth:2.5, color:'#FFF'};
  switch(p) {
    case 'instagram': return <Camera {...props}/>;
    case 'twitter': return <MessageCircle {...props}/>;
    case 'linkedin': return <Briefcase {...props}/>;
    case 'facebook': return <Users {...props}/>;
    default: return <Settings {...props}/>;
  }
};

const PLATFORMS = [
  { key:'instagram', label:'Instagram', color:'#E1306C', authType:'oauth', oauthPath:'/integrations/oauth/instagram', guide:'Requires a Facebook Developer App with Instagram Basic Display API enabled.' },
  { key:'twitter',   label:'Twitter/X', color:'#1DA1F2', authType:'token', guide:'Create a Twitter Developer App at developer.twitter.com and get a Bearer Token.' },
  { key:'linkedin',  label:'LinkedIn',  color:'#0077B5', authType:'token', guide:'Create a LinkedIn App at linkedin.com/developers with r_liteprofile + w_member_social permissions.' },
  { key:'facebook',  label:'Facebook',  color:'#1877F2', authType:'token', guide:'Use Facebook Graph API. Requires a Business page token with pages_manage_posts permission.' },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const [connected, setConnected] = useState([]);
  const [manualToken, setManualToken] = useState({});
  const [saving, setSaving] = useState({});
  const [msg, setMsg] = useState('');

  useEffect(() => {
    integrations.list().then(r => setConnected(Array.isArray(r) ? r : [])).catch(() => setConnected([]));
  }, []);

  const isConnected = (key) => connected.find(c => c.platform === key && c.is_active);

  const connectManual = async (platform) => {
    const token = manualToken[platform];
    if (!token?.trim()) { alert('Paste your access token first'); return; }
    setSaving(s => ({...s,[platform]:true}));
    try {
      await integrations.connect({ platform, access_token: token, account_name: `${platform} account` });
      const updated = await integrations.list();
      setConnected(updated);
      setManualToken(t => ({...t,[platform]:''}));
      setMsg(`${platform} connected ✓`);
      setTimeout(()=>setMsg(''),3000);
    } catch(e) { alert(e.response?.data?.error || 'Connection failed'); }
    setSaving(s => ({...s,[platform]:false}));
  };

  const disconnect = async (platform) => {
    if (!window.confirm(`Disconnect ${platform}?`)) return;
    await integrations.disconnect(platform);
    setConnected(await integrations.list());
  };

  const apiUrl = process.env.REACT_APP_API_URL || window.location.origin + '/api';

  return (
    <div className="page-enter">
      <div style={s.topbar}>
        <div>
          <div style={s.pageTitle}><Settings size={24} strokeWidth={3} style={{verticalAlign:'-3px'}}/> Settings</div>
          <div style={s.pageSub}>Connect your platforms and manage your account</div>
        </div>
        {msg && <span style={s.msgBadge}><Check size={16} strokeWidth={3} style={{verticalAlign:'-2px'}}/> {msg}</span>}
      </div>

      <div style={s.content}>
        {/* Account */}
        <div style={s.sectionTitle}>Account</div>
        <div style={s.card}>
          <div style={s.infoRow}><span style={s.infoLabel}>Name</span><span style={s.infoVal}>{user?.name}</span></div>
          <div style={s.infoRow}><span style={s.infoLabel}>Email</span><span style={s.infoVal}>{user?.email}</span></div>
          <div style={s.infoRow}><span style={s.infoLabel}>Plan</span><span style={{...s.infoVal, color:'#FF5C5C', fontWeight:800, textTransform:'uppercase', fontSize:14}}>{user?.plan || 'free'}</span></div>
        </div>

        {/* Integrations */}
        <div style={s.sectionTitle}>Platform Integrations</div>
        <div style={s.intGrid}>
          {PLATFORMS.map(p => {
            const conn = isConnected(p.key);
            return (
              <div key={p.key} style={{...s.intCard, borderColor: conn ? p.color : '#222'}}>
                <div style={s.intHeader}>
                  <div style={{display:'flex',alignItems:'center',gap:12}}>
                    <div style={{width:48,height:48,borderRadius:12,backgroundColor:p.color,border:'2px solid #222',boxShadow:'2px 2px 0px #222',display:'flex',alignItems:'center',justifyContent:'center'}}>{platformIcon(p.key)}</div>
                    <div>
                      <div style={s.intName}>{p.label}</div>
                      {conn && <div style={s.intConnName}>{conn.account_name}</div>}
                    </div>
                  </div>
                  {conn
                    ? <span style={s.connBadge}>Connected</span>
                    : <span style={s.notConnBadge}>Not connected</span>
                  }
                </div>

                {!conn && (
                  <div style={{marginTop:16}}>
                    {p.authType === 'oauth' ? (
                      <a href={`${apiUrl}${p.oauthPath}`} style={{...s.oauthBtn, background:p.color}}>
                        Connect via OAuth →
                      </a>
                    ) : (
                      <div>
                        <input
                          type="password"
                          placeholder="Paste access token"
                          value={manualToken[p.key]||''}
                          onChange={e => setManualToken(t=>({...t,[p.key]:e.target.value}))}
                          style={s.tokenInput}
                        />
                        <button onClick={()=>connectManual(p.key)} disabled={saving[p.key]} style={s.connectBtn}>
                          {saving[p.key] ? 'Connecting…' : 'Connect'}
                        </button>
                      </div>
                    )}
                    <div style={s.guide}>{p.guide}</div>
                  </div>
                )}

                {conn && (
                  <button onClick={()=>disconnect(p.key)} style={s.disconnectBtn}>
                    <Unplug size={14} strokeWidth={2.5} style={{verticalAlign:'-2px'}}/> Disconnect
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* AI Config */}
        <div style={s.sectionTitle}>AI Configuration</div>
        <div style={s.card}>
          <p style={s.aiNote}>
            AI features (post generation, content ideas, insights) require an API key set in your backend <code style={s.code}>.env</code> file.
          </p>
          <div style={s.infoRow}><span style={s.infoLabel}>Provider</span><span style={s.infoVal}>Anthropic Claude or OpenAI GPT-4o-mini</span></div>
          <div style={s.infoRow}><span style={s.infoLabel}>Set in backend</span><span style={{...s.infoVal, color:'#118AB2', fontFamily:'monospace', fontSize:14, fontWeight:700}}>ANTHROPIC_API_KEY or OPENAI_API_KEY</span></div>
        </div>

        {/* Deploy info */}
        <div style={s.sectionTitle}>Deployment</div>
        <div style={s.card}>
          <div style={s.deployStep}><span style={s.stepNum}>1</span><div><strong style={{color:'#222'}}>Backend</strong> — deploy <code style={s.code}>/backend</code> to Railway. Set all env vars from <code style={s.code}>.env.example</code></div></div>
          <div style={s.deployStep}><span style={s.stepNum}>2</span><div><strong style={{color:'#222'}}>Frontend</strong> — deploy <code style={s.code}>/frontend</code> to Vercel. Set <code style={s.code}>REACT_APP_API_URL</code> to your Railway URL</div></div>
          <div style={s.deployStep}><span style={s.stepNum}>3</span><div><strong style={{color:'#222'}}>Domain</strong> — add custom domain on Vercel. Update <code style={s.code}>FRONTEND_URL</code> in Railway</div></div>
          <div style={s.deployStep}><span style={s.stepNum}>4</span><div><strong style={{color:'#222'}}>OAuth</strong> — add your frontend URL as redirect URI in each platform's developer console</div></div>
        </div>
      </div>
    </div>
  );
}

const s = {
  topbar:{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'24px 32px',borderBottom:'3px solid #222',background:'#FFF'},
  pageTitle:{fontFamily:'"Fredoka",sans-serif',fontSize:26,fontWeight:700,letterSpacing:'-0.5px',color:'#222'},
  pageSub:{fontFamily:'"Nunito",sans-serif',fontSize:15,color:'#666',marginTop:4,fontWeight:600},
  msgBadge:{fontSize:13,fontFamily:'"Fredoka",sans-serif',color:'#06D6A0',fontWeight:800,display:'flex',alignItems:'center',gap:4},
  content:{padding:32,maxWidth:960},
  sectionTitle:{fontFamily:'"Fredoka",sans-serif',fontSize:18,fontWeight:700,marginBottom:16,marginTop:12,color:'#222',letterSpacing:'0.02em'},
  card:{background:'#FFF',border:'3px solid #222',boxShadow:'6px 6px 0px #222',borderRadius:16,padding:'24px',marginBottom:32},
  infoRow:{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'14px 0',borderBottom:'2px solid #222'},
  infoLabel:{fontSize:14,fontFamily:'"Fredoka",sans-serif',fontWeight:700,color:'#222'},
  infoVal:{fontSize:15,fontFamily:'"Nunito",sans-serif',fontWeight:600,color:'#444'},
  intGrid:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:24,marginBottom:32},
  intCard:{background:'#FFF',border:'3px solid #222',boxShadow:'4px 4px 0px #222',borderRadius:16,padding:'24px',transition:'border-color 0.1s'},
  intHeader:{display:'flex',alignItems:'center',justifyContent:'space-between'},
  intName:{fontSize:18,fontFamily:'"Fredoka",sans-serif',fontWeight:700,color:'#222'},
  intConnName:{fontSize:13,fontFamily:'"Nunito",sans-serif',fontWeight:700,color:'#666',marginTop:4},
  connBadge:{fontSize:11,background:'#06D6A0',border:'2px solid #222',color:'#222',padding:'4px 10px',borderRadius:20,fontWeight:800,fontFamily:'"Fredoka",sans-serif',boxShadow:'2px 2px 0px #222'},
  notConnBadge:{fontSize:11,background:'#EEE',border:'2px solid #222',color:'#666',padding:'4px 10px',borderRadius:20,fontWeight:800,fontFamily:'"Fredoka",sans-serif'},
  oauthBtn:{display:'inline-block',padding:'10px 18px',borderRadius:10,color:'#fff',textDecoration:'none',fontFamily:'"Fredoka",sans-serif',fontSize:14,fontWeight:700,border:'2px solid #222',boxShadow:'2px 2px 0px #222',transition:'all 0.1s'},
  tokenInput:{width:'100%',background:'#FFF',border:'2px solid #222',borderRadius:10,padding:'12px 14px',color:'#222',fontSize:14,fontFamily:'"Nunito",sans-serif',fontWeight:600,boxSizing:'border-box',marginBottom:12},
  connectBtn:{background:'#118AB2',border:'2px solid #222',boxShadow:'2px 2px 0px #222',borderRadius:10,padding:'10px 16px',color:'#FFF',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:'"Fredoka",sans-serif'},
  disconnectBtn:{marginTop:16,background:'#FFF',border:'2px solid #222',boxShadow:'2px 2px 0px #222',borderRadius:10,padding:'8px 14px',color:'#EF476F',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'"Fredoka",sans-serif'},
  guide:{fontSize:13,fontFamily:'"Nunito",sans-serif',fontWeight:600,color:'#666',marginTop:16,lineHeight:1.5},
  aiNote:{fontSize:15,fontFamily:'"Nunito",sans-serif',fontWeight:600,color:'#444',marginBottom:20,lineHeight:1.6},
  code:{background:'#FFF9EB',border:'2px solid #222',padding:'2px 8px',borderRadius:6,fontSize:13,color:'#118AB2',fontFamily:'monospace',fontWeight:700},
  deployStep:{display:'flex',gap:16,alignItems:'flex-start',padding:'16px 0',borderBottom:'2px solid #222',fontSize:15,fontFamily:'"Nunito",sans-serif',fontWeight:600,color:'#444',lineHeight:1.6},
  stepNum:{width:32,height:32,borderRadius:10,border:'2px solid #222',background:'#FFD166',boxShadow:'2px 2px 0px #222',color:'#222',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,fontFamily:'"Fredoka",sans-serif',fontWeight:800,flexShrink:0,marginTop:2},
};
